import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { TESTIMONIALS } from "../../data/mock";

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(
      (prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length,
    );
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-heading"
      aria-describedby="testimonials-description"
      className="py-24 bg-[var(--bg-section)] transition-theme overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
            Testimonials
          </span>
          <h2
            id="testimonials-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-6 leading-tight"
          >
            Loved by Schools Across Kenya
          </h2>
          <p
            id="testimonials-description"
            className="text-lg text-[var(--text-body)] opacity-90"
          >
            See what educators are saying about their experience with SEFIL 360.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Main Testimonial */}
          <div className="relative bg-[var(--bg-card)] rounded-3xl p-8 sm:p-12 border border-[var(--border-light)] shadow-xl">
            <Quote className="absolute top-6 left-6 w-12 h-12 text-[var(--accent-primary)]/20" />

            <div className="relative z-10">
              <p className="text-lg sm:text-xl text-[var(--text-body)] leading-relaxed mb-8 italic">
                "{TESTIMONIALS[currentIndex].content}"
              </p>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] flex items-center justify-center text-white font-bold text-lg">
                  {TESTIMONIALS[currentIndex].avatar}
                </div>
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)]">
                    {TESTIMONIALS[currentIndex].name}
                  </h4>
                  <p className="text-sm text-[var(--text-secondary)] font-medium">
                    {TESTIMONIALS[currentIndex].role}
                  </p>
                  <p className="text-sm text-[var(--accent-text)]">
                    {TESTIMONIALS[currentIndex].school}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={goToPrev}
              className="p-3 rounded-full bg-[var(--bg-card)] border border-[var(--border-light)] hover:border-[var(--accent-primary)] hover:bg-[var(--accent-wash)] transition-all duration-200"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5 text-[var(--text-primary)]" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "w-8 bg-[var(--accent-primary)]"
                      : "bg-[var(--border-medium)] hover:bg-[var(--accent-primary)]/50"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              className="p-3 rounded-full bg-[var(--bg-card)] border border-[var(--border-light)] hover:border-[var(--accent-primary)] hover:bg-[var(--accent-wash)] transition-all duration-200"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5 text-[var(--text-primary)]" />
            </button>
          </div>
        </div>

        {/* All Testimonials Grid (visible on larger screens) */}
        <div className="hidden lg:grid grid-cols-4 gap-4 mt-12">
          {TESTIMONIALS.map((testimonial, index) => (
            <button
              key={testimonial.id}
              onClick={() => {
                setIsAutoPlaying(false);
                setCurrentIndex(index);
              }}
              className={`p-4 rounded-xl text-left transition-all duration-300 ${
                index === currentIndex
                  ? "bg-[var(--accent-wash)] border-2 border-[var(--accent-primary)]"
                  : "bg-[var(--bg-card)] border border-[var(--border-light)] hover:border-[var(--accent-primary)]/30"
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    index === currentIndex
                      ? "bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white"
                      : "bg-[var(--bg-section)] text-[var(--text-secondary)]"
                  }`}
                >
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-medium text-sm text-[var(--text-primary)]">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {testimonial.role}
                  </p>
                </div>
              </div>
              <p className="text-xs text-[var(--text-body)] opacity-85 line-clamp-2">
                {testimonial.content.substring(0, 80)}...
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
